import React from "react";

import { Provider } from "react-redux";

import type { Match } from "react-router-dom";

import "react-router-dom";
import { getAccount, getEmployeeFormByFormId, getEmployeePolicyById } from "EntitiesApi/api";

import { adminEmployeeParam } from "../Dashboard/EmployeeUrls";
import PartnerProvider from "Common/PartnerProvider";

import { receiveAccount, receiveEmployeeForm, receiveEmployeePolicy } from "./actions";
import createStore, { type Store } from "./createStore";
import EmployeeDashboard from "./EmployeeDashboard";

type Props = {
  match: Match;
};

class App extends React.Component<Props> {
  store: Store;

  constructor(props: any) {
    super(props);
    this.store = createStore({
      initialDataLoaded: false,
      adminMode: !!props.match.params[adminEmployeeParam],
      employeeFormId: props.match.params[adminEmployeeParam],
    });
  }

  getEmployeePolicyIdFromResponse(response: any): string {
    if (response.enrolledEmployeePolicy) {
      return response.enrolledEmployeePolicy.id;
    }
    if (response.upcomingEmployeePolicy) {
      return response.upcomingEmployeePolicy.id;
    }
    if (response.cancelledEmployeePolicy) {
      return response.cancelledEmployeePolicy.id;
    }
    return "";
  }

  componentDidMount() {
    // No error handling on failure to fetch
    const accountFlags = ["enrolledCompanyPolicy", "upcomingCompanyPolicy", "enrolledEmployeePolicy", "upcomingEmployeePolicy", "cancelledEmployeePolicy"];
    this.store.dispatch(
      getAccount({
        include: accountFlags,
        onSuccess: (response, dispatch, _request) => {
          // This response brings the logged in account.
          // If the account is in admin mode, the response
          // IS NOT the employee account that they are viewing.
          dispatch(receiveAccount(response));
          // Flags used to get plan information on policy.
          const policyFlags = ["medicalPlan", "dentalPlan", "visionPlan"];

          // We will need to request a policy and it's plans.
          const dispatchEmployeePolicy = function (employeePolicyId) {
            return getEmployeePolicyById(employeePolicyId, {
              include: policyFlags,
              onSuccess: (response, _dispatch, _request) => {
                dispatch(receiveEmployeePolicy(response));
              },
            });
          };

          // If the account is in admin mode, we need to get the
          // employee account that they are viewing.
          if (this.props.match.params[adminEmployeeParam]) {
            const employeeFormId = this.props.match.params[adminEmployeeParam];
            const formFlags = ["enrolledEmployeePolicy", "upcomingEmployeePolicy"];
            dispatch(
              getEmployeeFormByFormId(employeeFormId, {
                include: formFlags,
                onSuccess: (response, dispatch, _request) => {
                  dispatch(receiveEmployeeForm(response));
                  dispatch(dispatchEmployeePolicy(this.getEmployeePolicyIdFromResponse(response)));
                },
              }),
            ); // The account is an actual employee viewing their own account.
          } else {
            dispatch(dispatchEmployeePolicy(this.getEmployeePolicyIdFromResponse(response)));
          }
        },
      }),
    );
  }

  render() {
    return (
      <Provider store={this.store}>
        <PartnerProvider>
          <EmployeeDashboard match={this.props.match} />
        </PartnerProvider>
      </Provider>
    );
  }
}

export default App;
